import { cloneDeep } from "lodash-es";
import { getOutputListForAllStep } from "@/flow/utils/getOutputListForAllStep";
import { getMetaoutputListForAllStep } from "@/flow/utils/getMetaoutputListForAllStep";
import {
  parseMetaoutputIndexFromName,
  parseOutputIndexFromName,
  rewriteMetaoutputNameIndex,
  rewriteOutputNameIndex,
} from "@/flow/utils/outputReferenceUtils";

/**
 * New UI: attach/detach + reconnect operations can reorder many steps without emitting
 * incremental "add/remove/move" events that `useSyncInputData` expects.
 *
 * Strategy:
 * - Persist `origId` inside output/metaoutput input-definition items (extra field OK).
 * - Rebuild the index portion of `name` based on current output/metaoutput lists.
 *
 * This makes output references resilient to ix/iy changes, router branches, and detach/attach.
 */
export function syncOutputIndexReferencesNewUi({
  prevSteps,
  nextSteps,
}: {
  prevSteps: any[];
  nextSteps: any[];
}): any[] {
  const prevOutputList = getOutputListForAllStep(prevSteps || [], {
    useStableIndex: true,
  });
  const nextOutputList = getOutputListForAllStep(nextSteps || [], {
    useStableIndex: true,
  });
  const prevMetaoutputList = getMetaoutputListForAllStep(prevSteps || [], {
    useStableIndex: true,
  });
  const nextMetaoutputList = getMetaoutputListForAllStep(nextSteps || [], {
    useStableIndex: true,
  });

  const buildByUiIndex = (list: any[]) => {
    const map = new Map<number, any>();
    list.forEach((o: any) => {
      if (typeof o?.index === "number") {
        map.set(o.index, o);
      }
    });
    return map;
  };

  const prevOutputByUiIndex = buildByUiIndex(prevOutputList);
  const nextOutputByUiIndex = buildByUiIndex(nextOutputList);
  const prevMetaoutputByUiIndex = buildByUiIndex(prevMetaoutputList);
  const nextMetaoutputByUiIndex = buildByUiIndex(nextMetaoutputList);
  const resolveByKnownIndex = (map: Map<number, any>, index: number) =>
    map.get(index) ?? map.get(index + 1);

  const nextOutputIndexByOrigId = new Map<string, number>();
  nextOutputList.forEach((o: any) => {
    if (o?.origId && typeof o.index === "number") {
      nextOutputIndexByOrigId.set(String(o.origId), o.index);
    }
  });

  const nextMetaoutputIndexByOrigId = new Map<string, number>();
  nextMetaoutputList.forEach((o: any) => {
    if (o?.origId && typeof o.index === "number") {
      nextMetaoutputIndexByOrigId.set(String(o.origId), o.index);
    }
  });

  const buildOrigIdSet = (list: any[]) => {
    const set = new Set<string>();
    list.forEach((item) => {
      if (item?.origId) {
        set.add(String(item.origId));
      }
    });
    return set;
  };

  const prevOutputOrigIds = buildOrigIdSet(prevOutputList);
  const nextOutputOrigIds = buildOrigIdSet(nextOutputList);
  const prevMetaoutputOrigIds = buildOrigIdSet(prevMetaoutputList);
  const nextMetaoutputOrigIds = buildOrigIdSet(nextMetaoutputList);

  const removedOutputOrigIds = new Set(
    Array.from(prevOutputOrigIds).filter((id) => !nextOutputOrigIds.has(id))
  );
  const removedMetaoutputOrigIds = new Set(
    Array.from(prevMetaoutputOrigIds).filter(
      (id) => !nextMetaoutputOrigIds.has(id)
    )
  );

  /**
   * Rewrites the reference list held in one action-definition value. Returns the
   * new string, or null when nothing changed so the caller leaves the original
   * string untouched.
   */
  const rewriteReferenceValue = (val: string): string | null => {
    if (!val.includes('"output"') && !val.includes('"metaoutput"')) return null;

    let parsed: any;
    try {
      parsed = JSON.parse(val);
    } catch {
      return null;
    }
    if (!Array.isArray(parsed)) return null;

    let changed = false;
    const filtered: any[] = [];

    parsed.forEach((input: any) => {
      if (!input || typeof input.name !== "string") {
        filtered.push(input);
        return;
      }

      if (input.type === "output") {
        let refOrigId =
          typeof input.origId === "string" && input.origId
            ? input.origId
            : null;

        if (!refOrigId) {
          const oldIndex = parseOutputIndexFromName(input.name);
          const fromPrev =
            typeof oldIndex === "number"
              ? resolveByKnownIndex(prevOutputByUiIndex, oldIndex)
              : null;
          const fromNext =
            typeof oldIndex === "number"
              ? resolveByKnownIndex(nextOutputByUiIndex, oldIndex)
              : null;
          refOrigId = fromPrev?.origId || fromNext?.origId || null;
          if (refOrigId) {
            input.origId = String(refOrigId);
            changed = true;
          }
        }

        if (refOrigId && removedOutputOrigIds.has(String(refOrigId))) {
          changed = true;
          return;
        }

        if (refOrigId) {
          const newIndex = nextOutputIndexByOrigId.get(String(refOrigId));
          if (typeof newIndex === "number") {
            const nextName = rewriteOutputNameIndex(input.name, newIndex);
            if (nextName !== input.name) {
              input.name = nextName;
              changed = true;
            }
          }
        }
        filtered.push(input);
        return;
        // eslint-disable-next-line
      } else if (input.type === "metaoutput") {
        let refOrigId =
          typeof input.origId === "string" && input.origId
            ? input.origId
            : null;

        if (!refOrigId) {
          const oldIndex = parseMetaoutputIndexFromName(input.name);
          const fromPrev =
            typeof oldIndex === "number"
              ? resolveByKnownIndex(prevMetaoutputByUiIndex, oldIndex)
              : null;
          const fromNext =
            typeof oldIndex === "number"
              ? resolveByKnownIndex(nextMetaoutputByUiIndex, oldIndex)
              : null;
          refOrigId = fromPrev?.origId || fromNext?.origId || null;
          if (refOrigId) {
            input.origId = String(refOrigId);
            changed = true;
          }
        }

        if (refOrigId && removedMetaoutputOrigIds.has(String(refOrigId))) {
          changed = true;
          return;
        }

        if (refOrigId) {
          const newIndex = nextMetaoutputIndexByOrigId.get(String(refOrigId));
          if (typeof newIndex === "number") {
            const nextName = rewriteMetaoutputNameIndex(input.name, newIndex);
            if (nextName !== input.name) {
              input.name = nextName;
              changed = true;
            }
          }
        }
        filtered.push(input);
        return;
      }

      filtered.push(input);
    });

    return changed ? JSON.stringify(filtered) : null;
  };

  /**
   * Visits every string value in the tree and rewrites it in place.
   *
   * This replaces a flatten/unflatten round trip (safe-flat) that was quadratic
   * in the number of nodes — its cycle detector compared each value against
   * every value seen so far — and lossy in three ways: a second reference to
   * the same object became the literal string "[Circular]"; an object whose
   * keys look numeric came back as an array; and a key named constructor,
   * prototype or __proto__ was skipped rather than descended, re-parenting
   * everything below it one level up. Walking in place avoids all three.
   *
   * `ancestors` guards against genuine cycles. Callers currently pass
   * JSON-round-tripped trees, which cannot contain any, so this is defence for
   * a future caller rather than a live concern.
   */
  const rewriteInPlace = (container: any, ancestors: Set<any>) => {
    if (ancestors.has(container)) return;
    ancestors.add(container);

    const visit = (key: any) => {
      const value = container[key];
      if (typeof value === "string") {
        const nextValue = rewriteReferenceValue(value);
        if (nextValue !== null) {
          container[key] = nextValue;
        }
        return;
      }
      if (value && typeof value === "object") {
        rewriteInPlace(value, ancestors);
      }
    };

    if (Array.isArray(container)) {
      for (let i = 0; i < container.length; i += 1) visit(i);
    } else {
      Object.keys(container).forEach((key) => visit(key));
    }

    ancestors.delete(container);
  };

  const cloned = cloneDeep(nextSteps);
  if (cloned && typeof cloned === "object") {
    rewriteInPlace(cloned, new Set());
  }

  return cloned;
}
